import { useState } from 'react';
import { Mail, Phone, Briefcase, Calendar, User, ChevronRight, ChevronLeft, Linkedin, MapPin, GraduationCap } from 'lucide-react';
import { toast } from 'react-toastify';
import { getFirstEmail, getAllEmails } from '../../utils/emailHelpers';

// Fonction pour extraire les initiales du nom complet
const getInitials = (fullName) => {
    if (!fullName) return '?';
    const parts = fullName.trim().split(' ');
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const ProcessBizDevListView = ({
  prospectsByStage,
  processSteps,
  selectedProspects,
  onSelectProspect,
  onNavigateToIndividual,
  onMoveToNextStage,
  onRefresh
}) => {
  const [activeTab, setActiveTab] = useState(processSteps[0]?._id || null);

  const currentStep = processSteps.find(step => step._id === activeTab);
  const currentStepIndex = processSteps.findIndex(step => step._id === activeTab);
  const nextStep = currentStepIndex >= 0 && currentStepIndex < processSteps.length - 1
      ? processSteps[currentStepIndex + 1]
      : null;
  const previousStep = currentStepIndex > 0
      ? processSteps[currentStepIndex - 1]
      : null;
  const allProspects = prospectsByStage[activeTab] || [];

  const handleProspectClick = (individualId) => {
    if (onNavigateToIndividual) {
      onNavigateToIndividual(individualId);
    }
  };

  const handleCheckboxClick = (e, prospectId) => {
    e.stopPropagation();
    if (onSelectProspect) {
      onSelectProspect(prospectId);
    }
  };

  const handleMoveToNextStage = async (e, prospectId) => {
    e.stopPropagation();
    if (onMoveToNextStage && nextStep) {
      try {
        await onMoveToNextStage(prospectId, nextStep._id);
        toast.success(`Prospect déplacé vers l'étape "${nextStep.name}" avec succès`);
        if (onRefresh) {
          onRefresh();
        }
      } catch (error) {
        console.error('Error moving prospect:', error);
        toast.error('Erreur lors du déplacement du prospect');
      }
    }
  };

  const handleMoveToPreviousStage = async (e, prospectId) => {
    e.stopPropagation();
    if (onMoveToNextStage && previousStep) {
      try {
        await onMoveToNextStage(prospectId, previousStep._id);
        toast.success(`Prospect déplacé vers l'étape "${previousStep.name}" avec succès`);
        if (onRefresh) {
          onRefresh();
        }
      } catch (error) {
        console.error('Error moving prospect:', error);
        toast.error('Erreur lors du déplacement du prospect');
      }
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Onglets */}
      <div className="border-b overflow-x-auto">
        <div className="flex min-w-max">
          {processSteps.map((step) => {
            const stepProspects = prospectsByStage[step._id] || [];
            const isActive = activeTab === step._id;

            return (
              <button
                key={step._id}
                onClick={() => setActiveTab(step._id)}
                className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  isActive
                    ? 'border-blue-600 text-blue-600 bg-blue-50'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <span>{step.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs ${
                    isActive
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {stepProspects.length}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* En-tête avec checkbox de sélection globale */}
      {allProspects.length > 0 && (
        <div className="px-4 py-3 bg-gray-50 border-b flex items-center">
          <input
            type="checkbox"
            checked={allProspects.length > 0 && allProspects.every(prospect => selectedProspects.includes(prospect._id))}
            onChange={(e) => {
              const allProspectIds = allProspects.map(p => p._id);
              if (e.target.checked) {
                // Ajouter tous les prospects de l'étape
                onSelectProspect && allProspectIds.forEach(id => {
                  if (!selectedProspects.includes(id)) {
                    onSelectProspect(id);
                  }
                });
              } else {
                // Désélectionner tous les prospects de l'étape
                allProspectIds.forEach(id => {
                  if (selectedProspects.includes(id)) {
                    onSelectProspect(id);
                  }
                });
              }
            }}
            className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
          />
          <span className="ml-3 text-sm text-gray-600 font-medium">
            Sélectionner tous les prospects ({allProspects.length})
          </span>
        </div>
      )}

      {/* Liste des prospects */}
      <div className="divide-y">
        {allProspects.length === 0 ? (
          <div className="p-12 text-center">
            <User className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <p className="text-gray-600">Aucun prospect dans cette étape</p>
          </div>
        ) : (
          allProspects.map((prospect) => {
            const individual = prospect.individual;
            const isSelected = selectedProspects.includes(prospect._id);

            return (
              <div
                key={prospect._id}
                className={`p-4 hover:bg-gray-50 transition-colors ${
                  isSelected ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center space-x-4">
                  {/* Checkbox */}
                  <div onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleCheckboxClick(e, prospect._id)}
                      className="h-5 w-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                    />
                  </div>

                  {/* Reste de la ligne - cliquable */}
                  <div
                    onClick={() => handleProspectClick(individual?._id)}
                    className="flex items-center space-x-4 flex-1 cursor-pointer"
                  >

                  {/* Photo de profil */}
                  <div className="flex-shrink-0">
                    {individual?.photo ? (
                      <img
                        src={individual.photo}
                        alt={individual?.Name}
                        className="h-12 w-12 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-12 w-12 rounded-full bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
                        <span className="text-white font-semibold text-base">
                          {getInitials(individual?.Name)}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Informations principales */}
                  <div className="flex-1 min-w-0">
                    {/* Nom avec LinkedIn */}
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-sm font-semibold text-blue-600 truncate mb-0">
                        {individual?.Name || 'Nom inconnu'}
                      </h3>
                      {individual?.linkedinUrl && (
                        <a
                          href={individual.linkedinUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                        >
                          <Linkedin size={14} />
                        </a>
                      )}
                    </div>

                    {/* JobTitle et Company */}
                    {(individual?.jobTitle || individual?.company) && (
                      <div className="flex items-center space-x-1.5 text-xs mb-1">
                        {individual?.jobTitle && (
                          <span className="font-semibold text-gray-900 truncate max-w-[250px]">
                            {individual.jobTitle}
                          </span>
                        )}
                        {individual?.jobTitle && individual?.company && (
                          <span className="text-gray-400">-</span>
                        )}
                        {individual?.company && (
                          <span className="text-gray-600 truncate">
                            {individual.company}
                          </span>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    {individual?.description && (
                      <div className="text-xs text-gray-600 mb-1 line-clamp-2 max-w-[500px]">
                        {individual.description}
                      </div>
                    )}

                    {/* Emails - sur une ligne dédiée */}
                    {(getAllEmails(individual?.professionalEmail).length > 0 || getAllEmails(individual?.personalEmail).length > 0 || individual?.email) && (
                      <div className="flex items-center space-x-3 text-xs mb-1">
                        {getAllEmails(individual?.professionalEmail).length > 0 && (
                          <div className="flex items-center space-x-1">
                            <Mail size={11} className="text-blue-500" />
                            <span className="truncate max-w-[180px]">
                              {getFirstEmail(individual.professionalEmail)}
                            </span>
                          </div>
                        )}
                        {getAllEmails(individual?.personalEmail).length > 0 && (
                          <div className="flex items-center space-x-1">
                            <Mail size={11} className="text-green-500" />
                            <span className="truncate max-w-[180px]">
                              {getFirstEmail(individual.personalEmail)}
                            </span>
                          </div>
                        )}
                        {/* Email générique (fallback) */}
                        {getAllEmails(individual?.professionalEmail).length === 0 && getAllEmails(individual?.personalEmail).length === 0 && individual?.email && (
                          <div className="flex items-center space-x-1">
                            <Mail size={11} className="text-gray-400" />
                            <span className="truncate max-w-[180px]">
                              {individual.email}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Téléphone - sur sa propre ligne */}
                    {individual?.phoneNumber &&
                     individual.phoneNumber !== '' &&
                     individual.phoneNumber !== '-' &&
                     individual.phoneNumber.toLowerCase() !== 'téléphone indisponible' && (
                      <div className="flex items-center space-x-1 text-xs mb-1">
                        <Phone size={11} className="text-gray-400" />
                        <span>{individual.phoneNumber}</span>
                      </div>
                    )}

                    {/* Autres informations */}
                    <div className="flex flex-wrap gap-2 text-xs text-gray-600">
                      {/* Ville */}
                      {individual?.city && (
                        <div className="flex items-center space-x-1">
                          <MapPin size={11} className="text-gray-400" />
                          <span>{individual.city.split(',')[0].trim()}</span>
                        </div>
                      )}

                      {/* Formation */}
                      {individual?.formation && (
                        <div className="flex items-center space-x-1">
                          <GraduationCap size={11} className="text-gray-400" />
                          <span className="truncate max-w-[200px]">{individual.formation}</span>
                        </div>
                      )}
                    </div>

                    {/* Tags */}
                    {prospect.tags && prospect.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {prospect.tags.slice(0, 3).map((tag, idx) => (
                          <span
                            key={idx}
                            className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full"
                          >
                            {tag.name}
                          </span>
                        ))}
                        {prospect.tags.length > 3 && (
                          <span className="text-xs text-gray-500 py-0.5">
                            +{prospect.tags.length - 3}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                    {/* Boutons de navigation et badge de l'étape */}
                    <div className="flex-shrink-0 flex items-center space-x-2">
                      {previousStep && (
                        <button
                          onClick={(e) => handleMoveToPreviousStage(e, prospect._id)}
                          className="p-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded-full transition-colors"
                          title={`Retour à ${previousStep.name}`}
                        >
                          <ChevronLeft size={14} />
                        </button>
                      )}
                      <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {currentStep?.name || 'N/A'}
                      </span>
                      {nextStep && (
                        <button
                          onClick={(e) => handleMoveToNextStage(e, prospect._id)}
                          className="p-1.5 bg-orange-600 hover:bg-orange-700 text-white rounded-full transition-colors"
                          title={`Passer à ${nextStep.name}`}
                        >
                          <ChevronRight size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Notes si présentes */}
                {prospect.notes && prospect.notes.length > 0 && (
                  <div className="mt-3 ml-16 text-xs text-gray-600 bg-yellow-50 p-2 rounded border-l-2 border-yellow-400">
                    <p className="line-clamp-2">{prospect.notes[prospect.notes.length - 1]}</p>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer avec statistiques */}
      {allProspects.length > 0 && (
        <div className="px-6 py-3 bg-gray-50 border-t text-sm text-gray-600">
          Affichage de {allProspects.length} prospect(s)
          {selectedProspects.length > 0 && (
            <span className="ml-4 text-blue-600 font-medium">
              {selectedProspects.length} sélectionné(s)
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default ProcessBizDevListView;
